import queryString from 'query-string'
import { isEmpty, isString, isArray, toArray } from 'lodash'

import {
  attachmentOptions,
  channelToTabIndexMap,
  sentimentOptions,
  CHANNEL_TYPE,
  CHANNELS_WITH_CUSTOMER_ID,
} from '@/utils/constants'
import { formatCallDuration, getArrayNormalized } from '@/utils/helpers'

/** Call Explorer flow
 *
 * 1. formatCallExplorerQueryString takes all the filters and appends them to the url params as a query string
 * 2. parseParams (on initial load) takes the url params and converts them back to usable filter values
 * 3. mapParamIds takes those values and maps them to the correct objects for the filters (e.g. {value: id, label: name})
 *
 * Right now, a new filter needs to be added to callSearch.redux, callSearch.actions, callSearch.helpers (this file - parseParams, formatCallExplorerQueryString, mapParamIds), CallSearchFilters, CallSearchPage, SavedFilterForm.schema, savedFilters.helpers, callColumns
 *
 * tl;dr - this is painful
 */

// Takes the URL param query string and convert it to usable filter values
export const parseParams = (searchStr) => {
  const parsed = queryString.parse(searchStr)
  const { call_ids, organizationId, channel } = parsed
  const formattedOrgId = Number(organizationId)

  // Ignore all other filters for call_id search, but don't append call_ids to call explorer page
  if (call_ids) {
    const filters = {
      call_ids: getArrayNormalized(call_ids, (item) => Number(item)),
      organizationId: formattedOrgId,
    }
    // If a channel query parameter is present, normalize it and include it in the filters
    if (channel) {
      filters.channel = isArray(channel) ? channel[0] : channel
    }
    if (parsed.scorecards) {
      filters.scorecards = getArrayNormalized(parsed.scorecards)
    }

    return filters
  }

  const initialFilters = {
    organizationId: formattedOrgId,
    startDate: new Date(parsed.startDate),
    endDate: new Date(parsed.endDate),
    dateRange: parsed.dateRange,
    callDuration: formatCallDuration(parsed.callDuration),
    maxCallDuration: parsed.maxCallDuration || '',
    isWin: parsed.isWin || '',
    includeCallsWithoutAudio: parsed.includeCallsWithoutAudio === 'true',

    // Arrays
    keywords: getArrayNormalized(parsed.keywords),
    keywordOptions: {
      logic: parsed.keywordSearchOperator || 'or',
      side: parsed.keywordSideFilter || 'both',
    },
    agents: getArrayNormalized(parsed.agents, (item) => Number(item)),
    tags: getArrayNormalized(parsed.tags, (item) => Number(item)),
    playbooks: getArrayNormalized(parsed.playbooks),
    // Such URLs already exist: the parameter used to be inert on message
    // channels, so a call-tab value could survive into a bookmark. Restoring it
    // now would filter on a hash email ingestion never writes.
    voipCustomerIds: CHANNELS_WITH_CUSTOMER_ID.includes(parsed.channel || CHANNEL_TYPE.CALL)
      ? getArrayNormalized(parsed.voipCustomerIds)
      : [],
    endingSentiment: getArrayNormalized(parsed.endingSentiment),
    attachments: getArrayNormalized(parsed.attachments),
    direction: parsed.direction || '',
    scorecards: getArrayNormalized(parsed.scorecards),

    // Events
    checklist: { selected: [], includes: true },
    deck: { selected: [], includes: true },
    postcall: { selected: [], includes: true },
    notifications: { selected: [], includes: true },
    dispositions: { selected: [], includes: true },

    // Omnichannel
    minMessageCount: parsed.minMessageCount || '',
    maxMessageCount: parsed.maxMessageCount || '',
    channel: parsed.channel,
  }

  if (parsed.scorecards) {
    initialFilters.scoredStatus = parsed.scoredStatus
    initialFilters.scorecardThreshold = parsed.scorecardThreshold
  }

  // TODO: This could just use the events code below.
  if (parsed.dispositionsFilters) {
    initialFilters.dispositions = {
      selected: getArrayNormalized(parsed.dispositionsFilters),
      includes: parsed.dispositionsIncludes !== 'false',
    }
  }

  const { events } = parsed
  if (!isEmpty(events)) {
    const parseEvent = (event) => {
      const [, section] = event.split(' - ')
      initialFilters[section].selected.push(event)
      initialFilters[section].includes = parsed[section] === 'true'
    }

    if (isString(events)) {
      parseEvent(events)
    } else {
      events.forEach(parseEvent)
    }
  }

  return initialFilters
}

// Converts all raw values from query params (such as agent=123) to the correct object for dropdowns (such as {value: 123, label: 'Mary Poppins'})
export const mapParamIds = (params) => (dispatch, getState) => {
  const { data } = getState().callSearch

  const updatedParams = {
    ...params,
    keywords: toArray(params.keywords).map((value) => ({ label: value, value })),
    agents: data.agents.filter((o) => toArray(params.agents).includes(o.value)),
    tags: data.tags
      .flatMap((tagCat) => tagCat.options)
      .filter((o) => toArray(params.tags).includes(o.value)),
    playbooks: data.playbooks.filter((o) => toArray(params.playbooks).includes(o.value)),
    voipCustomerIds: toArray(params.voipCustomerIds).map((value) => ({ label: value, value })),
    endingSentiment: sentimentOptions.filter((o) =>
      toArray(params.endingSentiment).includes(o.value)
    ),
    attachments: attachmentOptions.filter((o) => toArray(params.attachments).includes(o.value)),
    direction: params.direction || '',
    scorecards: data.scorecardConfigs
      .filter((o) => toArray(params.scorecards).includes(o.sid))
      .map((o) => ({ id: o.id, label: o.name, value: o.id, sid: o.sid })),
  }

  return updatedParams
}

const formatDropdownOptions = (options) => {
  if (isString(options)) {
    return options
  }

  if (isArray(options)) {
    return options?.map((option) => option.value)
  }
  return options.value
}

// Takes all the filters and appends them to the url params as a query string
export const formatCallExplorerQueryString = (filters) => {
  // Ignore all other filters for call_id search
  const { call_ids, organizationId, channel } = filters

  if (call_ids) {
    const body = { call_ids, organizationId }

    if (channel) {
      body.channel = channel
    }

    return queryString.stringify(body)
  }

  // Build query string for all other filters
  const body = {
    organizationId: filters.organizationId,
    startDate: filters.startDate,
    endDate: filters.endDate,
    dateRange: filters.dateRange || undefined,
    callDuration: formatCallDuration(filters.callDuration),
    maxCallDuration: filters.maxCallDuration || undefined,
    isWin: filters.isWin !== '' ? filters.isWin : undefined,
    includeCallsWithoutAudio: filters.includeCallsWithoutAudio,
    agentType: filters.agentType || undefined,

    // Arrays
    keywords: filters.keywords?.map(formatDropdownOptions),
    keywordSearchOperator: filters.keywordOptions?.logic,
    keywordSideFilter: filters.keywordOptions?.side,
    agents: filters.agents?.map(formatDropdownOptions),
    tags: filters.tags?.map(formatDropdownOptions),
    playbooks: filters.playbooks?.map(formatDropdownOptions),
    voipCustomerIds: filters.voipCustomerIds?.map(formatDropdownOptions),
    endingSentiment: filters.endingSentiment?.map(formatDropdownOptions),
    attachments: filters.attachments?.map(formatDropdownOptions),
    direction: filters.direction || undefined,
    scorecards: filters.scorecards?.map((scorecard) => scorecard.sid),

    // Omnichannel
    minMessageCount: filters.minMessageCount || undefined,
    maxMessageCount: filters.maxMessageCount || undefined,
    channel: filters.channel || undefined,
  }

  // TODO: This could just use the events code below.
  if (!isEmpty(filters.dispositions?.selected)) {
    body.dispositionsFilters = filters.dispositions.selected.map(formatDropdownOptions) || []
    body.dispositionsIncludes = filters.dispositions.includes ?? true
  }

  if (!isEmpty(filters.scorecards)) {
    body.scoredStatus = filters.scoredStatus || undefined
    body.scorecardThreshold = filters.scorecardThreshold || undefined
  }

  const events = []
  const sectionIncludes = {}
  const { checklist, deck, notifications, postcall } = filters
  const allSections = { checklist, deck, notifications, postcall }

  Object.entries(allSections).forEach(([section, values]) => {
    if (isEmpty(values)) {
      return
    }

    const { selected, includes } = values

    if (isEmpty(selected)) {
      return
    }

    events.push(...selected)
    sectionIncludes[section] = includes
  })

  if (!isEmpty(events)) {
    body.events = events
  }
  if (!isEmpty(sectionIncludes)) {
    Object.entries(sectionIncludes).forEach(([key, value]) => {
      body[key] = value
    })
  }

  return queryString.stringify(body)
}

export const getNestedPlaceholderText = (disabled, controlsVisible, showPill) => {
  const menuIsOpen = controlsVisible

  if (showPill) {
    return ''
  }

  if (disabled) {
    return 'None available'
  }

  if (menuIsOpen) {
    return 'Search...'
  }

  return 'Select item(s)'
}

export const getChannelTabIndex = (channel) => channelToTabIndexMap[channel] || 0
